import { bool, oneOfType, shape, string } from 'prop-types';
import _ from 'lodash';
import {
    Email as EmailIcon,
    GetApp as GetAppIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Schedule as ScheduleIcon,
} from '@material-ui/icons';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import messageDescriptor from '../../lingui/LinguiPropTypes';
import { MenuAction, MenuActionItem } from './Actions';
import { useReportDialog, REPORT_MODES } from '../report-dialog/ReportDialog';
import { SessionStorageUtilities } from '../../../utilities/StorageUtilities';
import { kabobToPascalCase } from '../../../utilities/StringUtilities';
import { useFilterState } from '../../../stores/FilterStateStore';
import {
    filterStateParam,
    buildUrlParamValue as buildFilterStateUrlParamValue,
} from '../../../utilities/FilterStateUrlUtilities';
import { usePageStateValues } from '../../../stores/PageStateStore';
import {
    pageStateParam,
    buildUrlParamValue as buildPageStateUrlParamValue,
} from '../../../utilities/PageStateUrlUtilities';
import { removePlmPrefix } from '../../../utilities/PgmUtilities';

const emailSubjectPrefix = t`[nGeniusPULSE Report]`;

function getSessionStorage(pageId) {
    // Only include session storage for the current page.  This assumes that all session storage
    // keys are prefixed with a pascal-case version of the page ID.  This will always be true
    // for panels that use the page framework provided panel ID as a prefix for their session
    // storage keys.
    const pageKeyPrefix = `${kabobToPascalCase(pageId)}-`;
    const pageSessionStorage = _.pickBy(SessionStorageUtilities.getDictionary(), (value, key) => {
        return key.startsWith(pageKeyPrefix);
    });

    // The report API will use JSON.stringify when hydrating the session storage, wo we have to
    // use JSON.parse here to ensure values are correctly represented in the report.
    return _.mapValues(pageSessionStorage, JSON.parse);
}

export default function ReportAction({ pageId, pageTitle, options, ...restProps }) {
    const [reportDialog, showReportDialog] = useReportDialog();
    const appliedOptions = { ...defaultOptions, ...options };
    const [filterState] = useFilterState();
    const [pageStateValues] = usePageStateValues();

    function showReportDialogWithMode(mode) {
        const urlPath = removePlmPrefix(appliedOptions.pageUrlPath || window.location.pathname);
        showReportDialog(mode, _.noop, {
            page: {
                pageId,
                urlProtocol: window.location.protocol.replace(':', ''),
                urlPath,
                urlParams: {
                    [filterStateParam]: buildFilterStateUrlParamValue(filterState?.encoded),
                    [pageStateParam]: buildPageStateUrlParamValue(pageStateValues),
                },
                sessionStorage: getSessionStorage(pageId),
            },
            email: {
                subject: `${i18n._(emailSubjectPrefix)}: ${i18n._(pageTitle)}`,
            },
        });
    }

    if (!appliedOptions.canDownload && !appliedOptions.canEmail && !appliedOptions.canSchedule) return null;

    return (
        <>
            <MenuAction
                icon={<PictureAsPdfIcon />}
                tooltip={t`Download, email, or schedule a PDF of this page`}
                {...restProps}
            >
                {appliedOptions.canDownload && (
                    <MenuActionItem
                        icon={<GetAppIcon />}
                        label={t`Download PDF Report`}
                        onClick={() => showReportDialogWithMode(REPORT_MODES.DOWNLOAD)}
                    />
                )}
                {appliedOptions.canEmail && (
                    <MenuActionItem
                        icon={<EmailIcon />}
                        label={t`Email PDF Report`}
                        onClick={() => showReportDialogWithMode(REPORT_MODES.EMAIL)}
                    />
                )}
                {appliedOptions.canSchedule && (
                    <MenuActionItem
                        icon={<ScheduleIcon />}
                        label={t`Schedule PDF Report`}
                        onClick={() => showReportDialogWithMode(REPORT_MODES.SCHEDULE)}
                    />
                )}
            </MenuAction>
            {reportDialog}
        </>
    );
}

export const reportActionOptionsType = shape({
    canDownload: bool,
    canEmail: bool,
    canSchedule: bool,
});

const defaultOptions = {
    canDownload: true,
    canEmail: true,
    canSchedule: true,
};

ReportAction.propTypes = {
    pageId: string,
    pageTitle: oneOfType([messageDescriptor, string]),
    options: reportActionOptionsType,
};

ReportAction.defaultProps = {
    pageId: undefined,
    pageTitle: undefined,
    options: defaultOptions,
};
